import { getAppFiles, uploadAppFiles } from '@/lib/collections/appFiles';
import { Camera, Loader2 } from 'lucide-react';
import React, { useRef, useState } from 'react';
import { toast } from 'sonner';

interface AvatarUploadFieldProps {
  /** Unique storage key for this vendor's avatar, e.g. `vendor-avatar-${walletAddress}` */
  fileId: string;
  value?: string;
  onUploaded: (url: string) => void;
  disabled?: boolean;
}

/**
 * Avatar image upload with live preview. Uploads to appFiles hosted storage
 * and reports the resulting public URL back to the parent form via onUploaded.
 */
export const AvatarUploadField: React.FC<AvatarUploadFieldProps> = ({
  fileId,
  value,
  onUploaded,
  disabled,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [preview, setPreview] = useState<string | undefined>(value);
  const [uploading, setUploading] = useState(false);

  // Keep preview in sync if the parent's saved value changes (e.g. on first load)
  React.useEffect(() => {
    setPreview(value);
  }, [value]);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Please choose an image file.');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image is too large (max 5MB).');
      return;
    }

    const localUrl = URL.createObjectURL(file);
    setPreview(localUrl);
    setUploading(true);

    try {
      const success = await uploadAppFiles(fileId, file);
      if (!success) {
        toast.error('Avatar upload failed. Please try again.');
        return;
      }
      const uploaded = await getAppFiles(fileId);
      if (uploaded?.url) {
        setPreview(uploaded.url);
        onUploaded(uploaded.url);
        toast.success('Avatar updated.');
      }
    } catch (error) {
      console.error('Avatar upload error', error);
      toast.error('Something went wrong uploading your avatar.');
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  return (
    <div className='flex items-center gap-4'>
      <button
        type='button'
        disabled={disabled || uploading}
        onClick={() => inputRef.current?.click()}
        className='group relative h-20 w-20 shrink-0 overflow-hidden rounded-full border-2 border-border bg-secondary disabled:cursor-not-allowed'
        aria-label='Upload avatar'
      >
        {preview ? (
          <img src={preview} alt='Avatar preview' className='h-full w-full object-cover' />
        ) : (
          <div className='flex h-full w-full items-center justify-center text-primary'>
            <Camera className='h-6 w-6' />
          </div>
        )}
        <div className='absolute inset-0 flex items-center justify-center bg-foreground/40 opacity-0 transition-opacity group-hover:opacity-100'>
          {uploading ? (
            <Loader2 className='h-5 w-5 animate-spin text-white' />
          ) : (
            <Camera className='h-5 w-5 text-white' />
          )}
        </div>
      </button>
      <div>
        <p className='text-sm font-semibold text-foreground'>Profile photo</p>
        <p className='text-xs text-muted-foreground'>PNG or JPG, up to 5MB.</p>
      </div>
      <input
        ref={inputRef}
        type='file'
        accept='image/*'
        className='hidden'
        onChange={handleFileChange}
        disabled={disabled || uploading}
      />
    </div>
  );
};

export default AvatarUploadField;
